// The weapon in a hero's hand and what each of its attacks will really hit for, so the hotbar can
// say "Axe attack · 4 dmg · reach 1" with the axe's own icon instead of a catalogue placeholder.
// The numbers are the sim's (`weapon_raw`, `aim_max`): weapon damage + power (Blessed / Weakened
// counted), spell base + focus, the main hand's reach or a missile's range. Guard and the sky still
// come off on the board; the target preview shows that figure once a foe is aimed at.
import { getConn } from '../net/connection';
import { catalogueItem } from '../items/catalogue';
import { getBridge } from '../encounter/session';
import { isCompanionId } from './companion';
import type { ActionDef } from './actions';
import type { UnitArms, UnitState } from './types';

/** What a hero holds in the main hand, as the hotbar draws and names it. */
export interface Held {
  /** The piece's own name ("Iron Hand Axe"); "bare hands" / "teeth and claws" when it has none. */
  name: string;
  /** The family in one word ("axe", "longbow"), for the tile's name. */
  kind: string;
  /** The piece's def (its kit icon, docs/129); absent when the hand is bare. */
  def?: number;
  /** A bow, crossbow or thrown piece: its blows are shots. */
  ranged: boolean;
  /** No piece in the hand (fists, or a companion's bite). */
  bare: boolean;
}

interface ItemRow { heroId: bigint; equippedSlot?: number | null; def: number; tier: number; name: string }

/** The item row in a hero's main hand, from the SDK's cache (the World screen holds every item row of the World). */
function mainHandRow(heroId: number): ItemRow | undefined {
  const c = getConn() as unknown as { db?: { item?: { iter(): Iterable<ItemRow> } } } | undefined;
  const rows = c?.db?.item?.iter();
  if (!rows) return undefined;
  const h = BigInt(heroId);
  for (const r of rows) if (r.heroId === h && r.equippedSlot === 0) return r;
  return undefined;
}

const FAMILY_WORDS: Record<string, string> = { Sword: 'sword', Axe: 'axe', Mace: 'mace', Dagger: 'dagger', Spear: 'spear', Bow: 'bow', Crossbow: 'crossbow', Staff: 'staff', Wand: 'wand' };
const NAME_KINDS: ReadonlyArray<[RegExp, string]> = [
  [/longbow/i, 'longbow'], [/crossbow/i, 'crossbow'], [/bow\b/i, 'bow'], [/greataxe/i, 'greataxe'], [/axe|hatchet/i, 'axe'], [/greatsword/i, 'greatsword'], [/longsword/i, 'longsword'],
  [/sword|blade|sabre|saber|falchion/i, 'sword'], [/hammer/i, 'hammer'], [/mace|club|flail/i, 'mace'], [/dagger|knife|dirk|stiletto/i, 'dagger'], [/spear|pike|lance|glaive|halberd/i, 'spear'],
  [/staff|stave/i, 'staff'], [/wand|rod/i, 'wand'], [/sling/i, 'sling'],
];

/** The weapon a unit attacks with; `undefined` for a foe (they have no item rows). */
export function heldWeapon(unit: UnitState | undefined): Held | undefined {
  if (!unit || unit.heroId === undefined) return undefined;
  const ranged = !!unit.arms?.ranged;
  if (isCompanionId(unit.heroId)) return { name: 'teeth and claws', kind: 'bite', ranged: false, bare: true };
  const row = mainHandRow(unit.heroId);
  if (!row) return { name: 'bare hands', kind: 'fists', ranged: false, bare: true };
  const cat = catalogueItem(row.def);
  const card = cat ? undefined : getBridge()?.itemCard(row.def, row.tier);
  const fam = card && typeof card.family === 'object' ? FAMILY_WORDS[String((card.family as Record<string, unknown>)['Weapon'])] : undefined;
  const kind = NAME_KINDS.find(([re]) => re.test(row.name))?.[1] ?? cat?.family ?? fam ?? 'weapon';
  return { name: row.name, kind, def: row.def, ranged, bare: false };
}

/** What an ability will hit for and how far it reaches, by this unit's own numbers. */
export interface Strength {
  /** Damage before the target's guard (and the sky's sway): `undefined` when it deals none. */
  damage?: number;
  /** The parts that make it up, in words ("axe 3 + power 1"). */
  parts?: string;
  /** How far it reaches: `reach N` for a blow, `range N` for a shot or spell. */
  range?: { n: number; melee: boolean };
  /** Its damage is the weapon's (Basic Attack, Strike, Aimed Shot): drawn and named after it. */
  weapon: boolean;
}

export function strengthOf(def: ActionDef, arms: UnitArms | undefined, held?: Held): Strength {
  const weapon = def.dmg?.kind === 'Weapon' || def.dmg?.kind === 'WeaponPlus';
  const out: Strength = { weapon };
  if (def.dmg && arms) {
    const mul = def.mul ?? 1;
    const what = held?.bare ? held.kind : held?.kind ?? 'weapon';
    switch (def.dmg.kind) {
      case 'Weapon': case 'WeaponPlus': {
        const plus = def.dmg.kind === 'WeaponPlus' ? def.dmg.n : 0;
        out.damage = Math.max(0, (arms.weapon + plus + arms.power) * mul);
        out.parts = [`${what} ${arms.weapon}`, plus ? `${plus > 0 ? '+' : '−'} ${Math.abs(plus)}` : '', arms.power ? `${arms.power > 0 ? '+' : '−'} power ${Math.abs(arms.power)}` : ''].filter(Boolean).join(' ') + (mul !== 1 ? `, ×${mul}` : '');
        break;
      }
      case 'Spell': out.damage = Math.max(0, def.dmg.n + arms.focus); out.parts = arms.focus ? `${def.dmg.n} ${arms.focus > 0 ? '+' : '−'} focus ${Math.abs(arms.focus)}` : undefined; break;
      case 'Flat': out.damage = def.dmg.n; break;
    }
  } else if (def.dmg && def.dmg.kind !== 'Weapon' && def.dmg.kind !== 'WeaponPlus') out.damage = def.dmg.n;
  if (def.reach === 'Weapon') out.range = arms?.ranged ? { n: arms.range, melee: false } : { n: arms?.reach ?? 1, melee: true };
  else if (def.reach === 'Reach') out.range = { n: arms?.reach ?? 1, melee: true };
  else if (def.reach === 'Tiles' && def.range > 0) out.range = { n: def.range, melee: def.range <= 1 };
  return out;
}

/** Basic Attack (ability 5) named for the hand that makes it: "Axe attack", "Bow shot", "Fists", "Bite". */
export const BASIC_ATTACK = 5;
export function attackName(held: Held | undefined): string {
  if (!held) return 'Attack';
  if (held.kind === 'bite') return 'Bite';
  if (held.kind === 'fists') return 'Punch';
  const k = held.kind.charAt(0).toUpperCase() + held.kind.slice(1);
  return held.ranged ? `${k} shot` : `${k} attack`;
}

/**
 * An ability as this hero has it: Basic Attack takes the weapon's name, every weapon attack (bar
 * the ones made with the off hand, a shield bash) takes the weapon's sprite.
 */
export function forUnit(def: ActionDef, unit: UnitState | undefined): ActionDef {
  if (!unit || def.item || !def.dmg || (def.dmg.kind !== 'Weapon' && def.dmg.kind !== 'WeaponPlus')) return def;
  const held = heldWeapon(unit);
  if (!held) return def;
  const offHand = def.requires?.includes('OffHand');
  const area = def.glyph === 'area';
  const out: ActionDef = { ...def };
  if (def.id === BASIC_ATTACK) out.name = attackName(held);
  // a blow from a melee hand (a sword, fists, a wolf's bite) is drawn as a blow, not the catalogue's shot
  if (def.id === BASIC_ATTACK && !held.ranged && out.glyph === 'beam') out.glyph = 'attack';
  if (!offHand && !area && held.def !== undefined) out.weaponIcon = { def: held.def, name: held.name };
  return out;
}
